import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import GroupIcon from '@mui/icons-material/Group'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'

import { AppBarR, Wrapper } from '../components/common'
import db from '../db/db'
import {
  sessionState,
  teamState,
  loadTeamMembers,
  loadTeamInvitations,
  sendTeamInvitation,
  removeTeamMember,
  acceptTeamInvitation,
  rejectTeamInvitation
} from '../state'
import Database from '../types/Database'

import routes from './routes'

/**
 * MyTeam - Team management page component
 *
 * Displays team members, pending invitations, and provides
 * functionality to invite new team members and manage team relationships.
 */
const MyTeam: React.FC = () => {
  const session = useRecoilValue(sessionState)
  const { user, isAuthenticating } = session
  const [team, setTeam] = useRecoilState(teamState)

  // Add Team Member form state
  const [inviteeEmail, setInviteeEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [isSendingInvitation, setIsSendingInvitation] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Remove Team Member state
  const [memberToRemove, setMemberToRemove] = useState<{
    id: string
    name: string
  } | null>(null)
  const [isRemovingMember, setIsRemovingMember] = useState(false)

  // Team member details state (for displaying names/emails)
  const [teamMemberDetails, setTeamMemberDetails] = useState<
    Map<string, Database.User>
  >(new Map())

  // Pending Invitations tab state
  const [invitationsTab, setInvitationsTab] = useState<'sent' | 'received'>(
    'received'
  )
  const [isProcessingInvitation, setIsProcessingInvitation] = useState(false)

  // Retry function for failed data loads
  const [retryKey, setRetryKey] = useState(0)
  const handleRetryLoadTeamData = () => {
    setRetryKey(prev => prev + 1)
  }

  // Load team data on mount
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!user?.uid) {
        console.log('[MyTeam] No user ID, skipping team data load')
        return
      }

      console.log('[MyTeam] Starting team data load for user:', user.uid)

      try {
        setTeam(prev => ({ ...prev, isLoadingTeam: true }))
        setErrorMessage('')

        // Load team members
        console.log('[MyTeam] Loading team members...')
        const members = await loadTeamMembers(user.uid)
        console.log('[MyTeam] Loaded team members:', members.length, members)

        // Load team invitations (sent and received)
        console.log('[MyTeam] Loading team invitations...')
        const invitations = await loadTeamInvitations(user.uid)
        console.log(
          '[MyTeam] Loaded invitations - sent:',
          invitations.sent.length,
          'received:',
          invitations.received.length
        )

        // Load user details for each team member
        console.log('[MyTeam] Loading user details for team members...')
        const userDetailsMap = new Map<string, Database.User>()
        for (const member of members) {
          // Get the other user's ID (not the current user)
          const otherUserId =
            member.userId1 === user.uid ? member.userId2 : member.userId1
          console.log(
            '[MyTeam] Loading user details for:',
            otherUserId,
            'from member:',
            member
          )

          try {
            // Query by uid field instead of using getById (which uses document ID)
            const userDocs = await db.query({
              collection: 'users',
              where: ['uid', '==', otherUserId]
            })
            console.log(
              '[MyTeam] User query result for',
              otherUserId,
              ':',
              userDocs
            )
            if (userDocs.length > 0) {
              const userDoc = userDocs[0]
              userDetailsMap.set(otherUserId, userDoc)
              console.log('[MyTeam] Added user to map:', otherUserId, userDoc)
            } else {
              console.warn('[MyTeam] No user doc found for:', otherUserId)
            }
          } catch (error) {
            console.error(
              `[MyTeam] Error loading user details for ${otherUserId}:`,
              error
            )
          }
        }

        console.log('[MyTeam] Final userDetailsMap size:', userDetailsMap.size)
        console.log(
          '[MyTeam] Final userDetailsMap contents:',
          Array.from(userDetailsMap.entries())
        )

        // Update state with all data at once, after user details are loaded
        setTeamMemberDetails(userDetailsMap)
        setTeam(prev => ({
          ...prev,
          teamMembers: members,
          sentInvitations: invitations.sent,
          receivedInvitations: invitations.received,
          isLoadingTeam: false
        }))

        console.log('[MyTeam] Team data load complete')
      } catch (error) {
        console.error('[MyTeam] Error loading team data:', error)
        const errorMsg =
          error instanceof Error
            ? error.message
            : 'Unable to load team data. Please check your connection and try again.'
        setErrorMessage(errorMsg)
        setTeam(prev => ({ ...prev, isLoadingTeam: false }))
      }
    }

    fetchTeamData()
  }, [user?.uid, setTeam, retryKey])

  // Handle sending team invitation
  const handleSendInvitation = async () => {
    if (!user || !user.uid) return

    // Clear previous messages
    setEmailError('')
    setSuccessMessage('')
    setErrorMessage('')

    // Validate email is not empty
    if (!inviteeEmail.trim()) {
      setEmailError('Email address is required')
      return
    }

    try {
      setIsSendingInvitation(true)

      const inviterName =
        `${user.firstname || ''} ${user.lastname || ''}`.trim() ||
        user.email ||
        'Unknown'

      await sendTeamInvitation(
        user.uid,
        user.email || '',
        inviterName,
        inviteeEmail.trim()
      )

      // Show success message
      setSuccessMessage(`Team invitation sent to ${inviteeEmail}`)
      setInviteeEmail('')

      // Reload team invitations to show the new sent invitation
      const invitations = await loadTeamInvitations(user.uid)
      setTeam(prev => ({
        ...prev,
        sentInvitations: invitations.sent,
        receivedInvitations: invitations.received
      }))
    } catch (error) {
      // Display error message
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to send invitation'
      setErrorMessage(errorMsg)
    } finally {
      setIsSendingInvitation(false)
    }
  }

  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInviteeEmail(e.target.value)
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('')
    }
  }

  // Handle Enter key down in email field
  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendInvitation()
    }
  }

  // Handle remove team member button click
  const handleRemoveClick = (memberId: string, memberName: string) => {
    setMemberToRemove({ id: memberId, name: memberName })
  }

  // Handle confirm remove team member
  const handleConfirmRemove = async () => {
    if (!user?.uid || !memberToRemove) return

    try {
      setIsRemovingMember(true)
      setErrorMessage('')
      setSuccessMessage('')

      await removeTeamMember(user.uid, memberToRemove.id)

      // Show success message
      setSuccessMessage(`${memberToRemove.name} removed from your team`)

      // Reload team members
      const members = await loadTeamMembers(user.uid)

      // Update team member details
      const userDetailsMap = new Map<string, Database.User>()
      for (const member of members) {
        const otherUserId =
          member.userId1 === user.uid ? member.userId2 : member.userId1
        try {
          // Query by uid field instead of using getById
          const userDocs = await db.query({
            collection: 'users',
            where: ['uid', '==', otherUserId]
          })
          if (userDocs.length > 0) {
            userDetailsMap.set(otherUserId, userDocs[0])
          }
        } catch (error) {
          console.error(`Error loading user details for ${otherUserId}:`, error)
        }
      }

      // Update state after all data is loaded
      setTeamMemberDetails(userDetailsMap)
      setTeam(prev => ({
        ...prev,
        teamMembers: members
      }))

      // Close dialog
      setMemberToRemove(null)
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to remove team member'
      setErrorMessage(errorMsg)
    } finally {
      setIsRemovingMember(false)
    }
  }

  // Handle cancel remove dialog
  const handleCancelRemove = () => {
    setMemberToRemove(null)
  }

  // Handle tab change for invitations
  const handleInvitationsTabChange = (
    _event: React.SyntheticEvent,
    newValue: 'sent' | 'received'
  ) => {
    setInvitationsTab(newValue)
  }

  // Handle accept invitation
  const handleAcceptInvitation = async (
    invitation: Database.TeamInvitation
  ) => {
    if (!user?.uid) return

    try {
      setIsProcessingInvitation(true)
      setErrorMessage('')
      setSuccessMessage('')

      await acceptTeamInvitation(invitation.id, invitation)

      // Show success message
      setSuccessMessage(
        `You are now team members with ${invitation.inviterName}`
      )

      // Reload team data
      const members = await loadTeamMembers(user.uid)
      const invitations = await loadTeamInvitations(user.uid)

      // Update team member details
      const userDetailsMap = new Map<string, Database.User>()
      for (const member of members) {
        const otherUserId =
          member.userId1 === user.uid ? member.userId2 : member.userId1
        try {
          // Query by uid field instead of using getById
          const userDocs = await db.query({
            collection: 'users',
            where: ['uid', '==', otherUserId]
          })
          if (userDocs.length > 0) {
            userDetailsMap.set(otherUserId, userDocs[0])
          }
        } catch (error) {
          console.error(`Error loading user details for ${otherUserId}:`, error)
        }
      }

      // Update state after all data is loaded
      setTeamMemberDetails(userDetailsMap)
      setTeam(prev => ({
        ...prev,
        teamMembers: members,
        sentInvitations: invitations.sent,
        receivedInvitations: invitations.received
      }))
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to accept invitation'
      setErrorMessage(errorMsg)
    } finally {
      setIsProcessingInvitation(false)
    }
  }

  // Handle reject invitation
  const handleRejectInvitation = async (invitationId: string) => {
    if (!user?.uid) return

    try {
      setIsProcessingInvitation(true)
      setErrorMessage('')
      setSuccessMessage('')

      await rejectTeamInvitation(invitationId)

      // Show confirmation message
      setSuccessMessage('Invitation declined')

      // Reload team invitations
      const invitations = await loadTeamInvitations(user.uid)
      setTeam(prev => ({
        ...prev,
        sentInvitations: invitations.sent,
        receivedInvitations: invitations.received
      }))
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to reject invitation'
      setErrorMessage(errorMsg)
    } finally {
      setIsProcessingInvitation(false)
    }
  }

  // Format date for display
  const formatDate = (date: Date | any): string => {
    if (!date) return 'Unknown date'

    const dateObj =
      date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date)

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Redirect unauthenticated users to sign-in page
  if (!isAuthenticating && !user) {
    return <Navigate to={routes.signin} replace />
  }

  // Show loading state during authentication
  if (isAuthenticating) {
    return (
      <Backdrop
        sx={{
          color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          zIndex: 9999,
          position: 'fixed',
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
        }}
        open={true}
      >
        <CircularProgress color='inherit' size={60} />
      </Backdrop>
    )
  }

  return (
    <>
      <AppBarR />
      <Wrapper>
        <Box sx={{ p: 2 }}>
          {/* Page Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <GroupIcon fontSize='small' />
            </Avatar>
            <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
              My Team
            </Typography>
          </Box>

          <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
            Manage your team members and invitations
          </Typography>

          {/* Loading State */}
          {team.isLoadingTeam && (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              minHeight='200px'
            >
              <CircularProgress />
              <Typography variant='body1' sx={{ ml: 2 }}>
                Loading team data...
              </Typography>
            </Box>
          )}

          {/* Error State with Retry */}
          {!team.isLoadingTeam && errorMessage && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Alert severity='error' sx={{ mb: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {errorMessage}
                  </Typography>
                </Box>
                <Typography variant='caption' color='text.secondary'>
                  Check your internet connection and try again.
                </Typography>
              </Alert>
              <Button variant='contained' onClick={handleRetryLoadTeamData}>
                Retry
              </Button>
            </Box>
          )}

          {/* Add Team Member Section */}
          {!team.isLoadingTeam && !errorMessage && (
            <Box>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <PersonAddIcon color='primary' />
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Add Team Member
                  </Typography>
                </Box>

                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 2 }}
                >
                  Invite a colleague to join your team by entering their email
                  address
                </Typography>

                {/* Success Message */}
                {successMessage && (
                  <Alert
                    severity='success'
                    sx={{ mb: 2 }}
                    onClose={() => setSuccessMessage('')}
                  >
                    {successMessage}
                  </Alert>
                )}

                {/* Error Message */}
                {errorMessage && (
                  <Alert
                    severity='error'
                    sx={{ mb: 2 }}
                    onClose={() => setErrorMessage('')}
                  >
                    {errorMessage}
                  </Alert>
                )}

                {/* Email Input and Send Button */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label='Email Address'
                    type='email'
                    value={inviteeEmail}
                    onChange={handleEmailChange}
                    onKeyDown={handleEmailKeyDown}
                    error={!!emailError}
                    helperText={emailError}
                    disabled={isSendingInvitation}
                    placeholder='colleague@example.com'
                    size='small'
                  />
                  <Button
                    variant='contained'
                    onClick={handleSendInvitation}
                    disabled={isSendingInvitation || !inviteeEmail.trim()}
                    sx={{ minWidth: '120px', height: '40px' }}
                  >
                    {isSendingInvitation ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Send Invite'
                    )}
                  </Button>
                </Box>
              </Paper>

              {/* Current Team Members Section */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <GroupIcon color='primary' />
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Current Team Members
                  </Typography>
                </Box>

                {team.teamMembers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <GroupIcon
                      sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                    />
                    <Typography variant='body2' color='text.secondary'>
                      No team members yet. Invite colleagues to build your team.
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {team.teamMembers.map(member => {
                      const otherUserId =
                        member.userId1 === user?.uid
                          ? member.userId2
                          : member.userId1
                      const userDetails = teamMemberDetails.get(otherUserId)

                      console.log(
                        '[MyTeam Render] Processing member:',
                        member.id
                      )
                      console.log('[MyTeam Render] Current user ID:', user?.uid)
                      console.log(
                        '[MyTeam Render] Member userId1:',
                        member.userId1,
                        'userId2:',
                        member.userId2
                      )
                      console.log('[MyTeam Render] Other user ID:', otherUserId)
                      console.log(
                        '[MyTeam Render] User details from map:',
                        userDetails
                      )
                      console.log(
                        '[MyTeam Render] teamMemberDetails map size:',
                        teamMemberDetails.size
                      )
                      console.log(
                        '[MyTeam Render] teamMemberDetails map keys:',
                        Array.from(teamMemberDetails.keys())
                      )

                      const displayName = userDetails
                        ? `${userDetails.firstname || ''} ${
                            userDetails.lastname || ''
                          }`.trim() ||
                          userDetails.email ||
                          'Unknown User'
                        : 'Loading...'
                      const displayEmail = userDetails?.email || ''

                      console.log('[MyTeam Render] Display name:', displayName)
                      console.log(
                        '[MyTeam Render] Display email:',
                        displayEmail
                      )

                      return (
                        <ListItem
                          key={member.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1
                          }}
                          secondaryAction={
                            <Button
                              variant='outlined'
                              color='error'
                              size='small'
                              startIcon={<PersonRemoveIcon />}
                              onClick={() =>
                                handleRemoveClick(member.id, displayName)
                              }
                            >
                              Remove
                            </Button>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {displayName.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={displayName}
                            secondary={displayEmail}
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                )}
              </Paper>

              {/* Pending Invitations Section */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <MailOutlineIcon color='primary' />
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Pending Invitations
                  </Typography>
                </Box>

                {/* Tabs for Sent and Received */}
                <Tabs
                  value={invitationsTab}
                  onChange={handleInvitationsTabChange}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab label='Received' value='received' />
                  <Tab label='Sent' value='sent' />
                </Tabs>

                {/* Received Invitations Tab */}
                {invitationsTab === 'received' && (
                  <>
                    {team.receivedInvitations.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <MailOutlineIcon
                          sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          No pending invitations
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {team.receivedInvitations.map(invitation => (
                          <ListItem
                            key={invitation.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              flexDirection: 'column',
                              alignItems: 'stretch'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                mb: 1
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                  {invitation.inviterName
                                    .charAt(0)
                                    .toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={invitation.inviterName}
                                secondary={`Invited on ${formatDate(
                                  invitation.createdAt
                                )}`}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                justifyContent: 'flex-end'
                              }}
                            >
                              <Button
                                variant='outlined'
                                color='error'
                                size='small'
                                startIcon={<CloseIcon />}
                                onClick={() =>
                                  handleRejectInvitation(invitation.id)
                                }
                                disabled={isProcessingInvitation}
                              >
                                Reject
                              </Button>
                              <Button
                                variant='contained'
                                color='primary'
                                size='small'
                                startIcon={<CheckIcon />}
                                onClick={() =>
                                  handleAcceptInvitation(invitation)
                                }
                                disabled={isProcessingInvitation}
                              >
                                Accept
                              </Button>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}

                {/* Sent Invitations Tab */}
                {invitationsTab === 'sent' && (
                  <>
                    {team.sentInvitations.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <MailOutlineIcon
                          sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          No pending invitations sent
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {team.sentInvitations.map(invitation => (
                          <ListItem
                            key={invitation.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'info.main' }}>
                                {invitation.inviteeEmail
                                  .charAt(0)
                                  .toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={invitation.inviteeEmail}
                              secondary={`Sent on ${formatDate(
                                invitation.createdAt
                              )}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Remove Team Member Confirmation Dialog */}
        <Dialog open={!!memberToRemove} onClose={handleCancelRemove}>
          <DialogTitle>Remove Team Member</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to remove {memberToRemove?.name} from your
              team? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelRemove} disabled={isRemovingMember}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemove}
              color='error'
              variant='contained'
              disabled={isRemovingMember}
            >
              {isRemovingMember ? <CircularProgress size={24} /> : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </Wrapper>
    </>
  )
}

export default MyTeam
